import { useState } from "react";
import { MessageCircle, MessageSquareQuote, ExternalLink, Eye, EyeOff } from "lucide-react";
import { Stars } from "./Stars";
import { fmtDate, fmtDecimal, toWhatsAppNumber } from "../utils/formatters";
import { RATING_EMOJIS } from "../constants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

/**
 * Linha de uma avaliação. Comentários longos quebram livremente (`break-words`)
 * e recebem destaque visual (borda lateral âmbar + fundo levemente tonalizado +
 * badge "Com comentário") — cliente que escreveu merece atenção.
 * Ordem das dimensões respeita config da campanha (passada via `dimensions`).
 *
 * Olhinho de "ocultar da média" exige CONFIRMAÇÃO via AlertDialog quando vai
 * esconder (operação que tira a nota da média geral — feature pensada pra
 * filtrar avaliações tendenciosas/maliciosas). Reativar é direto, sem confirmar.
 *
 * Props (3): review, dimensions, actions { onOpenPedido, onToggleHidden, hiddenPending }.
 */
export function ReviewItem({ review, dimensions = [], actions }) {
  const {
    pedido_id, pedido_codigo, customer_name, customer_phone,
    comment, rating_avg, ratings, submitted_at, hidden_from_avg,
  } = review;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const hasComment = !!(comment && comment.trim());
  const orderedRatings = orderRatings(ratings || {}, dimensions);
  const whatsappNumber = toWhatsAppNumber(customer_phone);
  // Código humano do pedido vem do backend via JOIN com pedidos. Fallback
  // pros 6 primeiros chars do UUID protege contra pedido hard-purged
  // (pedido_codigo virá null no LEFT JOIN).
  const codigoPedido = (pedido_codigo || "").replace(/^#/, "") || pedido_id?.slice(0, 6);
  const whatsappHref = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
        `Olá ${customer_name || ""}! Vi sua avaliação do pedido #${codigoPedido} e queria conversar com você.`
      )}`
    : null;

  const handleOpen = () => actions?.onOpenPedido?.(pedido_id);
  // Quando vai OCULTAR, abre confirmação. Quando vai REATIVAR, executa direto.
  const handleToggleClick = () => {
    if (hidden_from_avg) {
      actions?.onToggleHidden?.(review.id, true);
    } else {
      setConfirmOpen(true);
    }
  };
  const handleConfirmHide = () => {
    setConfirmOpen(false);
    actions?.onToggleHidden?.(review.id, false);
  };

  // Destaque visual quando há comentário: borda lateral âmbar + fundo levemente
  // tonalizado. Acumula com opacity-50 quando hidden.
  const liClass = [
    "px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 transition-all hover:bg-muted/30",
    hasComment ? "border-l-2 border-amber-500 bg-amber-500/5" : "",
    hidden_from_avg ? "opacity-50" : "",
  ].join(" ");

  return (
    <li className={liClass}>
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-sm">{customer_name || "Cliente"}</span>
          {codigoPedido && (
            <span className="text-[11px] font-mono text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
              #{codigoPedido}
            </span>
          )}
          {hasComment && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
              <MessageSquareQuote className="w-3 h-3" /> Com comentário
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">{fmtDate(submitted_at)}</span>
          {hidden_from_avg && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              fora da média
            </span>
          )}
        </div>

        {comment && (
          <p className="text-sm text-muted-foreground flex items-start gap-1.5 break-words whitespace-pre-wrap">
            <MessageCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="min-w-0 flex-1">{comment}</span>
          </p>
        )}

        {orderedRatings.length > 1 && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {orderedRatings.map(([k, v, label]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span>{label}:</span>
                <Stars value={v} />
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={handleOpen}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border bg-background hover:bg-muted transition-colors"
          >
            <ExternalLink className="w-3 h-3" /> Ver pedido
          </button>
          {whatsappHref && (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              <MessageCircle className="w-3 h-3" /> Entrar em contato
            </a>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0 sm:self-center">
        <div className="flex flex-col items-end shrink-0">
          <div className="inline-flex items-baseline gap-1">
            <span className="text-2xl font-bold text-amber-500">{fmtDecimal(rating_avg)}</span>
            <span className="text-xs text-muted-foreground">/ 5</span>
          </div>
          <Stars value={rating_avg} />
          <span className="text-base">{RATING_EMOJIS[Math.round(Number(rating_avg) || 0)] || ""}</span>
        </div>
        {/* Olhinho maior (h-10 w-10) e centralizado verticalmente no card. */}
        <button
          type="button"
          onClick={handleToggleClick}
          disabled={actions?.hiddenPending}
          title={hidden_from_avg ? "Voltar a contar na média geral" : "Ocultar da média geral"}
          aria-label={hidden_from_avg ? "Voltar a contar na média geral" : "Ocultar da média geral"}
          className={[
            "inline-flex items-center justify-center h-10 w-10 rounded-full border transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            hidden_from_avg
              ? "border-amber-500/40 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"
              : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
          ].join(" ")}
        >
          {hidden_from_avg ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ocultar avaliação da média geral?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa avaliação <strong>deixará de contar na nota média geral</strong>{" "}
              e nas médias por dimensão. Ela continua aparecendo na lista (marcada como{" "}
              <em>fora da média</em>), mas não influencia mais os números do relatório.
              <br /><br />
              Útil para filtrar avaliações tendenciosas ou feitas apenas para prejudicar.
              Você pode reverter a qualquer momento clicando no mesmo botão.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmHide}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Ocultar da média
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}

/** Ordena ratings respeitando `dimensions` da campanha; chaves órfãs no final. */
function orderRatings(ratings, dimensions) {
  const out = [];
  const seen = new Set();
  for (const d of dimensions || []) {
    if (d.key && ratings[d.key] != null) {
      out.push([d.key, ratings[d.key], d.label || cap(d.key)]);
      seen.add(d.key);
    }
  }
  for (const [k, v] of Object.entries(ratings)) {
    if (!seen.has(k)) out.push([k, v, cap(k)]);
  }
  return out;
}

const cap = (s) => (s || "").charAt(0).toUpperCase() + (s || "").slice(1);
